import type { SvgIconComponent } from '@mui/icons-material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BrandingWatermarkOutlinedIcon from '@mui/icons-material/BrandingWatermarkOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';

export type ProductFeature = {
    id: string;
    title: string;
    description: string;
    Icon: SvgIconComponent;
};

export type ProductPageSlug = 'reporteria' | 'inteligencia' | 'verificacion';

export type ProductPageContent = {
    slug: ProductPageSlug;
    path: string;
    navLabel: string;
    title: string;
    description: string;
    explainer: string;
    features: ProductFeature[];
    footerCta: {
        title: string;
        subtitle?: string;
        ctaLabel: string;
        ctaTo: string;
    };
};

const FEATURE_DETECCION: ProductFeature = {
    id: 'deteccion-de-marcas',
    title: 'Detección de marcas',
    description:
        'Encuentra menciones de marca en tus episodios y en clips de Instagram y TikTok, transcritas y analizadas automáticamente, para saber exactamente qué entregaste a cada avisador.',
    Icon: BrandingWatermarkOutlinedIcon,
};

const FEATURE_REPORTERIA: ProductFeature = {
    id: 'reporteria-exportable',
    title: 'Reportería exportable',
    description:
        'Genera reportes y dashboards listos para compartir con clientes o avisadores, con prueba concreta de lo que se entregó.',
    Icon: AssessmentOutlinedIcon,
};

const FEATURE_ECOSISTEMA: ProductFeature = {
    id: 'inteligencia-de-ecosistema',
    title: 'Inteligencia de ecosistema',
    description:
        'Ve qué marcas están activas y qué está pasando en todo el mercado: quién está pautando, en qué categoría, y cómo se mueve cada canal semana a semana.',
    Icon: TravelExploreOutlinedIcon,
};

const FEATURE_AUDIENCIA: ProductFeature = {
    id: 'medicion-de-audiencia',
    title: 'Medición de audiencia y livestreams',
    description:
        'Sigue views en YouTube, radio y programas de conversación, más viewers concurrentes y actividad de chat en vivo, comparando TV, radio y digital en un mismo panel.',
    Icon: LiveTvOutlinedIcon,
};

const FEATURE_VERIFICACION: ProductFeature = {
    id: 'verificacion-con-timestamp',
    title: 'Verificación con timestamp',
    description:
        'Confirma con un tercero independiente que una mención de marca ocurrió, con el segundo exacto y el alcance real de esa aparición.',
    Icon: AccessTimeOutlinedIcon,
};

const FEATURE_HISTORIAL: ProductFeature = {
    id: 'historial-de-datos',
    title: 'Historial de datos',
    description:
        'Accede a meses de histórico de marcas y programas para respaldar cualquier reporte con evidencia verificable en el tiempo, no solo con la semana actual.',
    Icon: HistoryOutlinedIcon,
};

export const PRODUCT_PAGES: Record<ProductPageSlug, ProductPageContent> = {
    reporteria: {
        slug: 'reporteria',
        path: '/producto/reporteria',
        navLabel: 'Reportería',
        title: 'Analiza y reporta tu contenido',
        description:
            'Detecta menciones de marca en episodios y clips, y genera reportes listos para compartir con avisadores y clientes.',
        explainer:
            'Si produces contenido y tienes marcas auspiciando episodios, necesitas poder demostrar exactamente qué entregaste, sin armar el reporte a mano cada mes. Podscope detecta cada mención automáticamente y la convierte en reportería lista para enviar.',
        features: [FEATURE_DETECCION, FEATURE_REPORTERIA],
        footerCta: {
            title: 'Empieza a reportar con datos verificados',
            ctaLabel: 'Agenda una demo',
            ctaTo: '/agenda-demo',
        },
    },
    inteligencia: {
        slug: 'inteligencia',
        path: '/producto/inteligencia',
        navLabel: 'Inteligencia de mercado',
        title: 'Descubre el ecosistema',
        description:
            'Mira qué marcas están activas, cómo se mueve cada canal y compara audiencia y livestreams de TV, radio y digital en un mismo panel.',
        explainer:
            'Si compras o planificas medios digitales, necesitas ver el mercado completo, no solo los canales con los que ya trabajas. Podscope te muestra qué marcas están activas, en qué categorías, y cómo se mueve cada canal, sin tener que rastrearlo manualmente.',
        features: [FEATURE_ECOSISTEMA, FEATURE_AUDIENCIA],
        footerCta: {
            title: 'Explora el ecosistema completo',
            ctaLabel: 'Agenda una demo',
            ctaTo: '/agenda-demo',
        },
    },
    verificacion: {
        slug: 'verificacion',
        path: '/producto/verificacion',
        navLabel: 'Verificación',
        title: 'Verifica menciones y audiencia',
        description:
            'Confirma menciones con timestamp y alcance real, y respalda reportes con histórico verificable de marcas y programas.',
        explainer:
            'Si te reportan que una marca apareció en un contenido, necesitas confirmarlo sin depender de la palabra del medio. Podscope verifica cada mención de forma independiente, con el segundo exacto y el histórico que la respalda.',
        features: [FEATURE_VERIFICACION, FEATURE_HISTORIAL],
        footerCta: {
            title: 'Verifica tu inversión en branded content',
            ctaLabel: 'Agenda una demo',
            ctaTo: '/agenda-demo',
        },
    },
};

/** Landing page bottom CTA (after use-case cards). Exact copy — do not paraphrase. */
export const LANDING_FOOTER_CTA = {
    title: 'Descubre lo que puedes hacer con Podscope',
    ctaLabel: 'Agenda una demo',
    ctaTo: '/agenda-demo',
} as const;

/** Landing-grid cards: each feature maps to its product page. */
export const PRODUCT_LANDING_CARDS: (ProductFeature & { pagePath: string })[] = [
    { ...FEATURE_DETECCION, pagePath: PRODUCT_PAGES.reporteria.path },
    { ...FEATURE_REPORTERIA, pagePath: PRODUCT_PAGES.reporteria.path },
    { ...FEATURE_ECOSISTEMA, pagePath: PRODUCT_PAGES.inteligencia.path },
    { ...FEATURE_AUDIENCIA, pagePath: PRODUCT_PAGES.inteligencia.path },
    { ...FEATURE_VERIFICACION, pagePath: PRODUCT_PAGES.verificacion.path },
    { ...FEATURE_HISTORIAL, pagePath: PRODUCT_PAGES.verificacion.path },
];

export const PRODUCT_NAV_ITEMS = Object.values(PRODUCT_PAGES).map(page => ({
    label: page.navLabel,
    to: page.path,
    Icon: page.features[0].Icon,
}));
